import { CommonModule } from '@angular/common';
import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { CurrencyBalances, RiskDistribution } from '@olv/ui-shared/models/wallet.model';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { balanceIconClass, balanceIcons, useFallbackIcon } from '@olv/ui-shared/utils/asset-balance.utils';
import { SplitPartsPipe } from '@olv/ui-shared/pipes/split-parts.pipe';

@Component({
  selector: 'app-risk-distribution-tooltip',
  standalone: true,
  templateUrl: './risk-distribution-tooltip.component.html',
  styleUrls: ['./risk-distribution-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class RiskDistributionTooltipComponent {
  protected readonly useFallbackIcon = useFallbackIcon;
  @Input() currency!: CurrencyBalances;
  @Input() baseCurrencyDisplayPrecision = 2;
  @Input() compact = false;

  private readonly metadata = inject(NetworkMetadataService);
  private splitParts = new SplitPartsPipe();

  constructor() {
    this.metadata.load();
  }

  get icons(): string[] {
    return balanceIcons(this.currency.currencyId, this.currency.currencySymbol, this.metadata);
  }

  get iconClass(): string {
    return balanceIconClass(this.currency.currencyId, this.currency.currencySymbol, this.metadata);
  }

  get riskDistribution(): RiskDistribution[] {
    return this.currency?.riskDistribution ?? [];
  }

  getRiskLabel(level: string): string {
    switch (level?.toLowerCase()) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'severe':
        return 'Severe';
      case 'notassessed':
      case 'not assessed':
      case 'not-assessed':
        return 'Not Assessed';
      case 'unknown':
        return 'Unknown';
      default:
        return level;
    }
  }

  getRiskColor(level: string): string {
    switch (level?.toLowerCase()) {
      case 'low':
        return '#59B57C';

      case 'medium':
        return '#E9C95B';

      case 'high':
        return '#D9534F';

      case 'severe':
        return '#A12B2B';

      case 'unknown':
        return '#1976D2';

      case 'notassessed':
      case 'not assessed':
      case 'not-assessed':
        return '#757575';

      default:
        return '#D9D9D9';
    }
  }

  formatAmount(value: number): string {
    const parts = this.splitParts.transform(value, this.currency.currencyDisplayPrecision);
    return parts.integer + parts.decimal;
  }

  formatBaseAmount(value: number): string {
    const parts = this.splitParts.transform(value, this.baseCurrencyDisplayPrecision, 'USD');
    return parts.integer + parts.decimal;
  }

  get totalBalance(): number {
    return this.currency?.availableAmountInCurrency ?? 0;
  }

  get totalBaseBalance(): number {
    return this.currency?.availableBalanceInBaseCurrency ?? 0;
  }

  get hasBaseCurrencyTotal(): boolean {
    const value = this.currency?.availableBalanceInBaseCurrency;
    return value !== undefined && value !== null;
  }
}
